import { useCallback, useMemo } from 'react';
import saveAs from 'file-saver';
import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import {
  exportInvoiceHistory,
  getInvoice,
  getInvoiceHistory,
  InvoiceModel,
  InvoiceSearchParams,
  InvoiceFilterType,
  getInvoiceQueryKey,
  invoiceDetailsCacheOptions,
  InvoiceDetailModel,
  updateInvoiceQueryCache,
  InvoiceResponse,
  InvoiceFilterOrderType,
  InvoiceQuickFilterOptions,
} from '@frontend/api-invoices';
import { payoutDescriptorQueries } from '@frontend/api-payouts';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { paymentsQueryKeys, useMultiQueryKey } from '@frontend/payments-hooks';
import { useAppScopeStore, useScopedInfiniteQuery } from '@frontend/scope';
import { isEmpty } from '@frontend/validation';
import { useAlert } from '@frontend/design-system';
import { getSearchParams } from './helpers';
import { useRefetchCurrentPageFirst } from './hooks';
import { useInvoiceShallowStore } from './invoice-provider';
import { calculateInvoicePageSummary } from './summary-helpers';

type FetchParams = {
  next: string;
  filter: InvoiceFilterType;
  numRows: number;
  currentPage: number;
  order: InvoiceFilterOrderType;
  locationIds: string[];
  selectedStatus: InvoiceQuickFilterOptions | undefined;
};

const fetchInvoices = async ({
  next,
  filter,
  numRows,
  currentPage,
  order,
  selectedStatus,
  locationIds,
}: FetchParams) => {
  let url;
  let params: InvoiceSearchParams = {};

  if (!next) {
    // initial url
    params = {
      ...getSearchParams(filter, locationIds, order, selectedStatus),
      limit: numRows,
      skip: (currentPage - 1) * numRows,
    };
    url = `payments/v1/search/invoices`;
  } else {
    url = new URL(`${appConfig.BACKEND_API}${next}`).toString();
  }

  return await getInvoiceHistory(url, params);
};

type FetchUserInvoiceRangeParams = {
  personId: string;
  dateRange: InvoiceFilterType['dateRange'];
  locationIds: string[];
  next?: string;
  numRows?: number;
  currentPage?: number;
  filter?: Omit<InvoiceFilterType, 'person' | 'dateRange'>;
  order?: InvoiceFilterOrderType;
};

const fetchUserInvoicesInRange = async ({
  personId,
  dateRange,
  locationIds,
  next,
  numRows = 100,
  currentPage = 1,
  filter = {},
  order,
}: FetchUserInvoiceRangeParams) => {
  const url = `payments/v1/search/invoices`;

  if (next) {
    const fullNextUrl = new URL(`${appConfig.BACKEND_API}${next}`).toString();

    return await getInvoiceHistory(fullNextUrl, {});
  }
  const params: InvoiceSearchParams = {
    ...getSearchParams({ ...filter, person: `id:${personId}`, dateRange }, locationIds, order),
    limit: numRows,
    skip: (currentPage - 1) * numRows,
  };
  return getInvoiceHistory(url, params);
};

export const useInvalidateInvoices = () => {
  const queryClient = useQueryClient();
  const invalidate = useCallback(
    (locationId: string | undefined) => {
      if (!locationId) return;

      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.includes(paymentsQueryKeys.invoices) && queryKey.includes(locationId),
        refetchActive: true,
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey.includes(paymentsQueryKeys.printInvoices) && queryKey.includes(locationId),
      });
    },
    [queryClient]
  );
  return invalidate;
};

/**
 * Retrieves paginated invoices based on the current filter, order, and location IDs.
 * Returns loading state, invoices, summary, and pagination functions.
 */

export const useQueryPaginatedInvoices = <T>({
  addPayoutDescriptor = false,
  select,
}: {
  addPayoutDescriptor?: boolean;
  select?: (data: InfiniteData<InvoiceResponse>) => InfiniteData<InvoiceResponse & T>;
} = {}) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  const { filter, currentPage, order, numRows, selectedStatus, setCurrentPage } = useInvoiceShallowStore(
    'filter',
    'numRows',
    'currentPage',
    'order',
    'selectedStatus',
    'setCurrentPage'
  );

  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const queryKey = getMultiQueryKey([paymentsQueryKeys.invoices, filter, numRows, order, selectedStatus]);

  const infiniteQueryProps = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchInvoices({
        next: pageParam,
        filter,
        numRows,
        currentPage: 1,
        order,
        locationIds,
        selectedStatus,
      }),
    getNextPageParam: (lastPage) => lastPage.meta?.links.next,
    select,
    staleTime: 60 * 1000,
    cacheTime: 2 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { refetchCurrentPageFirst, isFetchingPageRelevantData } = useRefetchCurrentPageFirst(
    infiniteQueryProps,
    currentPage,
    queryKey
  );

  const loading = infiniteQueryProps.isLoading || isFetchingPageRelevantData;

  const invoices = useMemo(
    () =>
      !loading && infiniteQueryProps?.data?.pages
        ? infiniteQueryProps?.data.pages[currentPage - 1]?.data?.invoices ?? []
        : [],
    [infiniteQueryProps?.data, loading, currentPage]
  );
  const { dataWithDescriptors: invoicesWithPayoutDescriptors } = payoutDescriptorQueries.useDataWithPayoutDescriptor({
    locationIds,
    enabled: addPayoutDescriptor,
    data: invoices,
  });

  return {
    loading,
    isFetching: infiniteQueryProps.isFetching,
    data: infiniteQueryProps.data,
    refetch: () => infiniteQueryProps.refetch(), // Just to keep API consistent with saga hook
    refetchCurrentPageFirst,
    invoices: invoicesWithPayoutDescriptors, // The backend returns a `null` if there are no items, instead of `[]`
    summary: infiniteQueryProps?.data?.pages?.[currentPage - 1]?.data?.summary,
    hasMore: infiniteQueryProps?.data?.pages?.[currentPage - 1]?.meta?.links?.next,
    fetchNextPage: () => {
      infiniteQueryProps?.fetchNextPage();
      setCurrentPage(currentPage + 1);
    },
    fetchPreviousPage: () => {
      setCurrentPage(Math.max(1, currentPage - 1));
    },
    infiniteQueryProps,
  };
};

/**
 * This is used for querying all invoices for the print preview
 * Reuses fetched invoices if no date filter applied (current page view)
 * Updates summary in print view to only include selected invoices
 */

export const useQueryAllInvoices = ({
  enable = true,
  addPayoutDescriptor = false,
}: { enable?: boolean; addPayoutDescriptor?: boolean } = {}) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const { filter, order, numRows, currentPage, selectedStatus } = useInvoiceShallowStore(
    'filter',
    'order',
    'numRows',
    'currentPage',
    'selectedStatus'
  );
  const NUM_ROWS = !!filter?.dateRange?.start ? 500 : numRows;
  const CUR_PAGE = !!filter?.dateRange?.start ? 1 : currentPage;
  const getQueryKey = () => {
    if (!!filter?.dateRange?.start) {
      return ['print-invoices', filter, NUM_ROWS, order, selectedStatus];
    } else {
      return ['invoices', filter, NUM_ROWS, order, selectedStatus];
    }
  };

  const { fetchNextPage, hasNextPage, data, isFetchingNextPage, isLoading, isFetching, isFetched } = useInfiniteQuery({
    enabled: enable,
    queryKey: getMultiQueryKey(getQueryKey()),
    queryFn: ({ pageParam }) =>
      fetchInvoices({
        next: pageParam,
        filter,
        numRows: NUM_ROWS,
        currentPage: CUR_PAGE,
        order,
        locationIds,
        selectedStatus,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.meta?.links.next;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  if (hasNextPage && !isFetchingNextPage && !!filter.dateRange?.start) {
    fetchNextPage();
  }

  const loading = isLoading || isFetching || isFetchingNextPage;

  const invoices = useMemo(() => {
    return (
      (!!filter?.dateRange?.start || currentPage != 1
        ? data?.pages?.flatMap((page) => page.data?.invoices ?? []).filter((x): x is InvoiceModel => !!x)
        : data?.pages?.[currentPage - 1]?.data?.invoices) ?? []
    );
  }, [filter?.dateRange?.start, data, currentPage]);

  const { dataWithDescriptors: invoicesWithPayoutDescriptors } = payoutDescriptorQueries.useDataWithPayoutDescriptor({
    locationIds,
    enabled: addPayoutDescriptor,
    data: invoices,
  });

  const summary = isEmpty(filter)
    ? invoices?.length
      ? calculateInvoicePageSummary(invoices)
      : undefined
    : data?.pages?.[currentPage - 1]?.data?.summary;

  return {
    loading,
    isFetched,
    invoices: invoicesWithPayoutDescriptors,
    summary,
  };
};

export const useExportInvoicesQuery = () => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const { filter, order, selectedStatus } = useInvoiceShallowStore('filter', 'order', 'selectedStatus');
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const {
    data: invoiceCSV,
    mutate: exportInvoices,
    isLoading: isExporting,
  } = useMutation({
    mutationKey: getMultiQueryKey([paymentsQueryKeys.invoiceExport, filter, order, selectedStatus]),
    mutationFn: () => exportInvoiceHistory(getSearchParams(filter, locationIds, order, selectedStatus)),
    onSuccess: (invoiceCSV: Blob) => {
      alerts.success(t('We’re working on exporting your data. It will download shortly.'));
      saveAs(invoiceCSV, 'request-history.csv');
    },
    onError: (err) => {
      alerts.error(t('Failed to export Request history'));
      console.error(err);
    },
  });

  return { invoiceCSV, isExporting, exportInvoices };
};

/**
 * Retreives a single invoice by its ID.
 */

export const useGetInvoiceQuery = (invoiceId?: string, retries?: number) => {
  const { accessibleLocationIds: allLocations } = useAppScopeStore();

  const queryKey = getInvoiceQueryKey(invoiceId!);
  const { isLoading, isError, data, isFetched, refetch } = useQuery({
    queryKey,
    queryFn: () => getInvoice(invoiceId!, allLocations),
    enabled: !!invoiceId,
    ...invoiceDetailsCacheOptions,
    retry: retries ?? 1,
    retryDelay: 1000,
  });

  return {
    invoice: data,
    isLoading,
    isError,
    isFetched,
    refetch,
    queryKey,
  };
};

export const useGetInvoiceNonQuery = (invoiceId?: string) => {
  const { accessibleLocationIds: allLocations } = useAppScopeStore();

  const callback = useCallback(
    (invoiceOverrideId?: string) => {
      return getInvoice(invoiceOverrideId || invoiceId!, allLocations);
    },
    [invoiceId, allLocations]
  );

  return callback;
};

export const useUpdateInvoiceQueryCache = (invoiceId?: string) => {
  const queryClient = useQueryClient();

  const changeCallback = useCallback(
    (toSet: InvoiceDetailModel) => {
      if (!invoiceId) {
        return console.warn('No invoiceId provided to update invoice query cache. Operation aborted.');
      }
      updateInvoiceQueryCache(queryClient, toSet, getInvoiceQueryKey(invoiceId));
    },
    [invoiceId]
  );

  return changeCallback;
};

/**
 * User Invoices in Range Query
 * This query is used to fetch invoices for a specific user within a date range.
 * It is primarily used for displaying user-specific invoices in a range, such as for a dashboard or report.
 */

export type UserInvoicesInRangeProps = {
  personId: string;
  dateRange: InvoiceFilterType['dateRange'];
  filter?: Omit<InvoiceFilterType, 'person' | 'dateRange'>;
  order?: InvoiceSearchParams['order'];
};

const INVOICE_RANGE_QUERY_KEY = 'user-invoices-range';

export const useInvalidateUserInvoicesInRange = () => {
  const queryClient = useQueryClient();

  return (personId: string) => {
    queryClient.invalidateQueries({
      predicate({ queryKey }) {
        return queryKey.includes(INVOICE_RANGE_QUERY_KEY) && queryKey.includes(personId);
      },
    });
  };
};

export const useUserInvoicesInRange = ({ personId, dateRange, filter, order }: UserInvoicesInRangeProps) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);

  const queryKey = getMultiQueryKey([INVOICE_RANGE_QUERY_KEY, personId, dateRange, filter, order]);

  const { data, isLoading, refetch, hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } =
    useScopedInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) =>
        fetchUserInvoicesInRange({ next: pageParam, personId, dateRange, filter, order, locationIds }),
      enabled: !!personId,
      getNextPageParam: (lastPage) => {
        return lastPage.meta?.links.next;
      },
    });

  if (hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  const loading = isLoading || isFetching || isFetchingNextPage;

  const invoices =
    data?.pages?.flatMap((page) => page.data?.invoices ?? []).filter((x): x is InvoiceModel => !!x) || [];

  return {
    isLoading: loading,
    invoices,
    refetch,
  };
};
